/**
 * Maxiom Apps 2026 house voice for the SEO/campaign landing factory.
 *
 * LandingPage.js should render through `resolveLandingVoice(page)` so new
 * registry entries inherit the voice without 175 hand-written essays.
 *
 * Allowed proof (until Antonio confirms more): since 2002, $100M+ contract
 * value, 221+ clients, Clutch, named case studies that already exist on
 * maxiomtech.com. Forbidden: 500+ projects, 98% satisfaction, 30+ years,
 * 200+ apps/skills, unattributed % gains, Inc. 5000, stock quotes.
 *
 * Location honesty: DC / VA / MD read as HQ. Every other city is
 * serve-from-DC: no rented local office.
 */

export const CASE_STUDY_TEASERS = [
  {
    name: "AT&T",
    url: "https://www.maxiomtech.com/case-study/att/",
  },
  {
    name: "Microsoft",
    url: "https://www.maxiomtech.com/case-study/microsoft/",
  },
  {
    name: "BMW Performance Center",
    url: "https://www.maxiomtech.com/case-study/bmw-performance-center/",
  },
  {
    name: "Toastmasters International",
    url: "https://www.maxiomtech.com/case-study/toastmasters-international/",
  },
];

export const HQ_LOCATION_SLUGS = new Set([
  "app-development-company-washington-dc",
  "app-development-company-virginia",
  "app-development-company-maryland",
  "app-development-company-richmond",
  "app-development-company-baltimore",
]);

const DEFAULT_TRUST = [
  "App studio since 2002",
  "$100M+ contract value · 221+ clients",
  "Clutch-rated (AT&T, Microsoft, BMW, Toastmasters)",
];

export function isHqLocation(page) {
  if (!page) return false;
  if (HQ_LOCATION_SLUGS.has(page.slug)) return true;
  const region = String(page.location?.region || "").toUpperCase();
  return region === "DC" || region === "VA" || region === "MD";
}

export function isHqSlug(slug) {
  return HQ_LOCATION_SLUGS.has(slug);
}

function flatten(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

function isKeywordCompanyHeadline(headline) {
  return /development company/i.test(flatten(headline));
}

function subjectNoun(page) {
  const raw = page.shortTitle || page.title || "this work";
  return raw.replace(/\s+Company$/i, "").trim();
}

function cityName(page) {
  return page.location?.city || "this city";
}

/**
 * Strip forbidden proof while leaving the sentence readable.
 * Applied to any factory-rendered string from landingPages.js.
 */
export function sanitizeLandingCopy(input) {
  if (input == null || typeof input !== "string") return input;
  let s = input;

  s = s.replace(/Inc\.?\s*5000\s*\+\s*/gi, "");
  s = s.replace(/Inc\.?\s*5000(?:\s+recognized(?:\s+(?:app development )?company)?)?/gi, "");
  s = s.replace(
    /from the team behind 200\+\s+shipped (apps|products)/gi,
    "from senior engineers shipping since 2002",
  );
  s = s.replace(
    /the team behind 200\+\s+shipped (apps|products)/gi,
    "senior engineers shipping since 2002",
  );
  s = s.replace(
    /the team behind 200\+\s+(apps|products)/gi,
    "senior engineers shipping since 2002",
  );
  s = s.replace(
    /senior engineers behind 200\+\s+shipped products/gi,
    "senior engineers shipping since 2002",
  );
  s = s.replace(
    /who have launched 100\+\s+cross-platform apps/gi,
    "who ship production React Native",
  );
  s = s.replace(
    /who have shipped 200\+\s+(apps|products)/gi,
    "who have been shipping since 2002",
  );
  s = s.replace(
    /who have scoped, shipped, and supported 200\+\s+apps[^.]*/gi,
    "who have been shipping since 2002",
  );
  s = s.replace(
    /calibrated against the 200\+\s+apps we've actually shipped/gi,
    "calibrated against work we've actually shipped since 2002",
  );
  s = s.replace(
    /calibrated against the 200\+\s+apps we've shipped/gi,
    "calibrated against work we've shipped since 2002",
  );
  s = s.replace(/across 200\+\s+apps/gi, "since 2002");
  s = s.replace(/200\+\s+shipped (apps|products)/gi, "work shipped since 2002");
  s = s.replace(/200\+\s+web apps shipped to production/gi, "production web apps since 2002");
  s = s.replace(
    /200\+\s+mobile, web, and AI applications/gi,
    "221+ clients on mobile, web, and AI",
  );
  s = s.replace(/Operators who have shipped 200\+\s+products/gi, "Operators shipping since 2002");
  s = s.replace(/200\+\s+(apps|products|web apps)/gi, "221+ clients");
  s = s.replace(
    /Launched 100\+\s+cross-platform apps/gi,
    "Production React Native, iOS, and Android",
  );
  s = s.replace(/100\+\s+cross-platform apps/gi, "production React Native");
  s = s.replace(/500\+\s+(successful\s+)?(projects|engagements)/gi, "221+ clients");
  s = s.replace(/98%\s+(client\s+)?satisfaction(\s+rate)?/gi, "");
  s = s.replace(/30\+\s+years(?:\s+(?:of\s+)?experience)?/gi, "since 2002");

  s = s.replace(/\s{2,}/g, " ");
  s = s.replace(/\s+,/g, ",");
  s = s.replace(/,\s*,/g, ",");
  s = s.replace(/\+\s*\+/g, "+");
  s = s.replace(/\.\s*\+/g, ".");
  s = s.replace(/\s+\./g, ".");
  s = s.replace(/^\s*[+|·—–-]\s*/g, "");
  s = s.replace(/\s*[+|·]\s*$/g, "");
  return s.trim();
}

export function resolveHeroHeadline(page) {
  if (page.heroDisplayHeadline) return page.heroDisplayHeadline;
  const raw = page.heroHeadline || page.title || "";
  if (!isKeywordCompanyHeadline(raw)) return raw;

  if (page.category === "location") {
    const city = cityName(page);
    if (isHqLocation(page)) {
      return `App development in ${city}.\nFrom the studio that lives here.`;
    }
    return `App development for ${city}.\nBuilt from DC, not a local office.`;
  }

  if (page.category === "industry") {
    return `${subjectNoun(page)} for people who already know the workflow.`;
  }

  return `${subjectNoun(page)}.\nWe'll build it, or we'll tell you not to.`;
}

export function resolveHeroSubheadline(page) {
  const original = sanitizeLandingCopy(page.heroSubheadline || "");
  if (page.pageType === "campaign") return original;
  if (page.category === "location" && !isHqLocation(page)) {
    const city = cityName(page);
    return `We serve ${city} from our Ashburn, VA studio (DC Metro). Senior engineers, hours overlap when it helps, travel for kickoff when a workshop would actually change the architecture. We do not rent a ${city} office to look local.`;
  }
  return original;
}

export function resolveTrustBullets(page) {
  if (page.pageType === "campaign") {
    const cleaned = (page.trustBullets || [])
      .map(sanitizeLandingCopy)
      .filter(Boolean);
    return cleaned.length > 0 ? cleaned : DEFAULT_TRUST;
  }
  if (page.category === "location" && !isHqLocation(page)) {
    return [
      `Serve ${cityName(page)} from our DC Metro studio`,
      "Since 2002 · $100M+ contract value · 221+ clients",
      "Clutch-rated (AT&T, Microsoft, BMW, Toastmasters)",
    ];
  }
  if (page.category === "location") {
    return [
      "Headquartered in Ashburn, VA (DC Metro)",
      "Since 2002 · $100M+ contract value · 221+ clients",
      "Clutch-rated (AT&T, Microsoft, BMW, Toastmasters)",
    ];
  }
  const cleaned = (page.trustBullets || [])
    .map(sanitizeLandingCopy)
    .filter((b) => b && !/inc\.?\s*5000|500\+|98%|200\+\s+apps|30\+\s+years/i.test(b));
  if (cleaned.length >= 3) return cleaned.slice(0, 3);
  return DEFAULT_TRUST;
}

export function resolvePrimaryCta(page) {
  const raw = page.primaryCtaLabel || "";
  if (page.pageType === "campaign") {
    return raw || "Start Your Free Assessment";
  }
  if (/\bquote\b/i.test(raw) || /supercharge/i.test(raw) || /bring your vision/i.test(raw)) {
    return "Schedule a Call";
  }
  return raw || "Schedule a Call";
}

export function resolveLeadFormHeadline(page) {
  const raw = page.leadFormHeadline || "";
  if (/\bquote\b/i.test(raw)) {
    return "Schedule a call with a senior engineer";
  }
  return raw || "Get a Free Consultation";
}

export function resolveLeadFormSubheadline(page) {
  const raw = sanitizeLandingCopy(page.leadFormSubheadline || "");
  if (!raw) {
    return "A senior engineer replies within one business day. No deck theater.";
  }
  return raw;
}

function resolveLongFormBody1(page) {
  return sanitizeLandingCopy(page.longFormBody1 || "");
}

function resolveLongFormBody2(page) {
  const original = sanitizeLandingCopy(page.longFormBody2 || "");
  if (page.category === "location" && !isHqLocation(page)) {
    const city = cityName(page);
    return `Maxiom Apps is an app studio in Ashburn, VA (DC Metro), shipping since 2002. ${city} product work (mobile, web, and AI) is designed and built from that studio. We overlap your hours when we can and get on a plane for kickoff when the session would actually change the architecture. We do not staff a ${city} office to look local.`;
  }
  return original;
}

function resolveFaqs(page) {
  const hq = isHqLocation(page);
  const city = cityName(page);
  return (page.faqs || []).map((faq) => {
    const q = sanitizeLandingCopy(faq.q);
    let a = sanitizeLandingCopy(faq.a);
    if (page.category === "location" && !hq) {
      if (/based in/i.test(q)) {
        a = `No. Our studio is in Ashburn, VA (DC Metro). ${city} work is built from there. We travel for kickoff and hard workshops when it actually helps. We don't staff a branded office to look local.`;
      } else if (/on-site/i.test(q) || /meet on-site/i.test(q)) {
        a = `When a workshop is worth the trip, yes. Day-to-day delivery is from the DC Metro: standups, PRs, and demos with the engineers who write the code.`;
      } else if (/local office|are you (actually )?local/i.test(q)) {
        a = `We serve ${city} from DC. Same senior engineers, same review, not a franchise and not a rented storefront.`;
      }
    }
    return { q, a };
  });
}

export function resolveLandingVoice(page) {
  return {
    heroEyebrow: page.heroEyebrow,
    heroHeadline: resolveHeroHeadline(page),
    heroSubheadline: resolveHeroSubheadline(page),
    trustBullets: resolveTrustBullets(page),
    primaryCtaLabel: resolvePrimaryCta(page),
    leadFormHeadline: resolveLeadFormHeadline(page),
    leadFormSubheadline: resolveLeadFormSubheadline(page),
    longFormHeading: sanitizeLandingCopy(page.longFormHeading),
    longFormBody1: resolveLongFormBody1(page),
    longFormBody2: resolveLongFormBody2(page),
    faqs: resolveFaqs(page),
    benefits: (page.benefits || []).map((b) => ({
      ...b,
      title: sanitizeLandingCopy(b.title),
      desc: sanitizeLandingCopy(b.desc),
    })),
    offerings: (page.offerings || []).map((o) => ({
      ...o,
      title: sanitizeLandingCopy(o.title),
      desc: sanitizeLandingCopy(o.desc),
    })),
    metaDescription: sanitizeLandingCopy(page.metaDescription),
    ogDescription: sanitizeLandingCopy(page.ogDescription || page.metaDescription),
    isHqLocation: isHqLocation(page),
  };
}

/**
 * Non-HQ location maps must not imply a Maxiom office in that metro.
 * Stats on those maps used invented local headcount: replace with allowed proof.
 */
export function sanitizeLocationMapConfig(slug, config) {
  if (!config) return config;
  if (isHqSlug(slug)) return config;

  const pins = (config.pins || []).map((p) => {
    if (p.type !== "hq") return p;
    const label = p.label && !/\bHQ\b/i.test(p.label) ? p.label : "Metro";
    return { ...p, label };
  });

  const badges = (config.badges || []).map((b, i) => {
    const blob = `${b.title || ""} ${b.subtitle || ""}`;
    if (i === 1 || /on-site|weekly|every sprint/i.test(blob)) {
      return { ...b, title: "Travel when useful", subtitle: "Not a local office" };
    }
    return b;
  });

  return {
    ...config,
    subtitle: [config.subtitle, "Served from DC Metro"].filter(Boolean).join(" · "),
    legendHqLabel: "Metro",
    pins,
    stats: [
      { value: "DC", label: "Studio" },
      { value: "2002", label: "Founded" },
      { value: "221+", label: "Clients" },
      { value: "ET", label: "Overlap" },
    ],
    badges,
  };
}

export function resolveLandingShowcase(page, data) {
  if (!data) return null;
  const heading = sanitizeLandingCopy(data.heading);
  const description = sanitizeLandingCopy(data.description);
  const bullets = (data.bullets || []).map((b) => sanitizeLandingCopy(b));
  let visualProps = data.visualProps ? { ...data.visualProps } : {};
  if (page.category === "location" && visualProps.config) {
    visualProps = {
      ...visualProps,
      config: sanitizeLocationMapConfig(page.slug, visualProps.config),
    };
  }

  if (page.category === "location" && !isHqLocation(page)) {
    const city = cityName(page);
    const kept = bullets
      .filter((b) => !/on-site presence|on-site work|on-site collaboration|on-site every/i.test(b))
      .slice(0, 2);
    return {
      ...data,
      visualProps,
      label: `Serving ${city} from DC`,
      heading: `${city} work. DC Metro studio.`,
      description: `We do not run a ${city} office. Apps for ${city} teams are designed and shipped from Ashburn, VA. ${description}`,
      bullets: [
        "No local franchise and no rented storefront.",
        "Kickoff travel when a workshop is worth the trip.",
        ...kept,
      ].slice(0, 4),
    };
  }

  return { ...data, heading, description, bullets, visualProps };
}
